import { useEffect, useMemo, useState } from 'react';
import { machinesApi, videosApi } from '../../../lib/apis';
import { Video } from '../../../types/videos';

export function useAssignVideos(machineId: string, onSaved?: () => void) {
  const [allVideos, setAllVideos] = useState<Video[]>([]);
  const [assigned, setAssigned] = useState<Video[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [allRes, asgRes] = await Promise.all([
          videosApi.get<{ data: Video[] }>(`/videos`),
          machinesApi.get<{ data: Video[] }>(`/machines/${machineId}/videos`),
        ]);
        const all = (allRes as any)?.data || (allRes as any);
        const asg = (asgRes as any)?.data || (asgRes as any);
        setAllVideos(all);
        setAssigned(asg);
      } catch (e: any) {
        setError(e.message || 'Failed to load videos');
      } finally {
        setLoading(false);
      }
    })();
  }, [machineId]);

  const unassigned = useMemo(() => {
    const assignedIds = new Set(assigned.map(v => v.id));
    return allVideos.filter(v => !assignedIds.has(v.id));
  }, [allVideos, assigned]);

  const assign = (id: string) => {
    const v = allVideos.find(x => x.id === id);
    if (!v) return;
    setAssigned(prev => {
      if (prev.some(p => p.id === id)) return prev; // prevent duplicates
      return [...prev, v];
    });
  };

  const unassign = (id: string) => {
    setAssigned(prev => prev.filter(v => v.id !== id));
  };

  const move = (id: string, dir: -1 | 1) => {
    setAssigned(prev => {
      const idx = prev.findIndex(v => v.id === id);
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = prev.slice();
      const [item] = copy.splice(idx, 1);
      copy.splice(nextIdx, 0, item);
      return copy;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const videoIds = assigned.map(v => v.id);
      await machinesApi.put(`/machines/${machineId}/videos`, { videoIds });
      onSaved && onSaved();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return {
    state: { allVideos, assigned, unassigned, loading, error, saving },
    actions: { assign, unassign, move, save },
  };
}
